import { Component, OnInit, Pipe, ViewChild, ElementRef, ViewEncapsulation, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { DashBoardService } from 'app/main/pages/dashboard/dashboard.services';
import { ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective } from "ng2-charts";
import { makeDrawValuePlugin } from '../chartjs.plugins';
import { DestroyableComponent } from 'app/main/destroyable.component';
import { takeUntil } from 'rxjs/operators';
import chartColors from './../chart-colors';

@Component({
    selector: 'revenue',
    templateUrl: './revenue.component.html',
    styleUrls: ['../dashboard.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RevenueComponent extends DestroyableComponent implements OnInit, AfterViewInit{
	public chartData: any;
	public showLoader: boolean;

	@ViewChild(BaseChartDirective, { static: false }) chartComponent: BaseChartDirective;

	/**
	 * Chart Ticket Year
	*/
	public barChartOptions: ChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		onResize(chart: any): void{
			// chart.js 4: onResize scatta presto, chartArea/colori possono non esserci ancora
			if(!chart.chartArea) return;
			chart.data.datasets.forEach((chartDataset: any) => {
				const bgColors: string[] = <Array<string>>chartDataset.hoverBackgroundColor;
				if(!Array.isArray(bgColors)) return;
				const gradients = [];
				for(let i = 0; i<bgColors.length; i++){
					const bgColor = bgColors[i];
					const gradient: CanvasGradient = chart.ctx.createLinearGradient(0, chart.chartArea.bottom/1.7, 0, chart.chartArea.bottom);
					gradient.addColorStop(0, bgColor);
					gradient.addColorStop(1, 'white');
					gradients.push(gradient);
				}
				chartDataset.backgroundColor = gradients;
			});
			/**
			 * when data or style changes update chart
			 */
			chart.update();
		},
		scales: {
			x: {},
			y: {
				beginAtZero: true,
				grid: {
					display: false,
				},
				ticks: {
					callback: function (value): string{
						return Number(value).toLocaleString('US');
					}
				}
			}
		},
		plugins: {
			tooltip: {
				cornerRadius: 8,
				padding: { x: 8, y: 6 },
				caretSize: 0,
				caretPadding: 10,
				backgroundColor: '#b5b5b5',
				displayColors: false,
				callbacks: {
					label: function(tooltipItem: any): string {
						const n = Number(tooltipItem.parsed.y);
						if(!n || isNaN(n)) return '';
						return n.toLocaleString('US');
					},
					title: function(item): any{
						return;
					},
				},
			}
		}
	};
	public chartPlugins = [makeDrawValuePlugin()];
	public barChartType: ChartType = 'bar';
	public barChartLegend = false;

	constructor(public dashboardService: DashBoardService){
		super();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void{
		this.chartData = { labels: [], datasets: [{data: []}] };
	}

	ngAfterViewInit(): void{
		this.dashboardService.checkoutDateResponse
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe({
			next: (response: any) => {
				if(this.dashboardService.groupingBy.value !== 'checkout_date') return;
				if(response) this.onData(response);
			}
		});
		this.dashboardService.travelDateResponse
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe({
			next: (response: any) => {
				if(this.dashboardService.groupingBy.value !== 'departure_date') return;
				if(response) this.onData(response);
			}
		});
	}
	// -----------------------------------------------------------------------------------------------------

	public onData(response: any): void{
		if(!response){
			this.showLoader = true;
			return;
		}
		const result = [];
		for (const item of response) {
			if(!item.year) continue;
			if(!result[item.year]) result[item.year] = 0;
			result[item.year] += Number(item.total_amount);
		}

		const values = [];
		const label: string[] = [];
		const colors = [];
		const gradients = [];
		const years = Object.keys(result);
		for (let y = 0; y < years.length; y++){
			const year = years[y];
			const colorIndex = years.length - y - 1;
			values.push(result[year]);
			label.push(String(year));
			colors.push(chartColors[colorIndex]);

			const chartArea = this.chartComponent.chart.chartArea;
			const gradient: CanvasGradient = this.chartComponent.chart.ctx.createLinearGradient(0, chartArea.bottom/1.7, 0, chartArea.bottom);
			gradient.addColorStop(0, chartColors[colorIndex]);
			gradient.addColorStop(1, 'white');
			gradients.push(gradient);
		}
		if(values.length === 0){
			this.showLoader = true;
			this.chartData = { labels: [], datasets: [{data: []}] };
		} else {
			this.showLoader = false;
			this.chartData = {
				labels: label,
				datasets: [
					{
						data: values,
						backgroundColor: gradients,
						borderColor: colors,
						hoverBackgroundColor: colors
					}
				]
			};
		}
	}
}
